import React, { useRef } from "react"
import { FlatList, FlatListProps, ListRenderItem, StyleProp, StyleSheet, ViewStyle } from "react-native"
import VisibilitySensor from "./visibility-sensor"
import impressionAggregator from "./impression-aggregator"

type Props = FlatListProps<any> & {
  id: string;
  data: any[];
  renderItem: (ListRenderItem<any>);
  keyExtractor: (item: any, index: number) => string;
  contentContainerStyle?: StyleProp<ViewStyle>;
  source: string;
}

const CompanyViewFlatlist: React.FC<Props> = ({
  id, data, renderItem, keyExtractor, contentContainerStyle={}, source, ...props
}: Props) => {
  const flatListRef = useRef(null)
  const lastSeenViewableItems = useRef(null)
  const visibleRef = useRef(false)

  const onViewableItemsChanged = useRef((viewableItems) => {
    // always remember the latest set so we can replay it on focus/visibility
    lastSeenViewableItems.current = viewableItems
    if (!visibleRef.current) return

    const viewables = viewableItems.viewableItems
    for (const item of viewables) {
      const companyImpression = {
        companyId: item.item._id,
        companyName: item.item.name,
        index: item.index,
        source: source,
      }
      impressionAggregator.company.add(companyImpression)
    }
  })

  return (
    <VisibilitySensor
      onChange={(visible) => {
        visibleRef.current = visible
        if (visible && lastSeenViewableItems.current) {
          onViewableItemsChanged?.current?.(lastSeenViewableItems.current)
        }
      }}
    >
      {(visible) => {
        visibleRef.current = visible
        return (
          <FlatList
            ref={flatListRef}
            onLayout={() => {
              // force an interaction to ensure originalViewableItems is set
              flatListRef.current?.recordInteraction()
            }}
            data={data}
            horizontal
            contentContainerStyle={[styles.flatListContainer, contentContainerStyle]}
            keyExtractor={keyExtractor}
            renderItem={renderItem}
            {...props}
            onViewableItemsChanged={onViewableItemsChanged.current}
            viewabilityConfig={{
              itemVisiblePercentThreshold: 25,
            }}
          />
        )
      }}
    </VisibilitySensor>
  )
}

export default CompanyViewFlatlist

const styles = StyleSheet.create({
  flatListContainer: {
    gap: 10,
    paddingHorizontal: 5,
    paddingVertical: 5,
  },
})